import { ToplistGame } from "@/components/grow/games/toplist/ToplistGame";
import { FavoriteDomain } from "@/graphql/generated/operations";
import { parseToplistSnapshot, TOPLIST_KEEP, TOPLIST_SNAPSHOT_VERSION } from "./toplist/snapshot";
import type { GameDefinition, GameGroupDefinition } from "./types";

// What every toplist game shares; an instance differs only in its key, its
// copy and the catalogue domain it ranks.
function toplistGame(game: {
  key: string;
  domain: FavoriteDomain;
  titleId: string;
  descriptionId: string;
}): GameDefinition {
  return {
    ...game,
    snapshotVersion: TOPLIST_SNAPSHOT_VERSION,
    rules: { keep: TOPLIST_KEEP },
    target: { kind: "favorites", domain: game.domain },
    acceptsSnapshot: (value) => parseToplistSnapshot(value, TOPLIST_KEEP) !== null,
    Component: ToplistGame,
  };
}

// Every game Grow · Play offers, keyed for code like FEATURE_CATALOG; `key` is
// the game key genesis knows. genesis refuses a key whose kind or instance it
// does not know, so a game appears here only once genesis offers it.
export const GAMES = {
  toplistMovies: toplistGame({
    key: "toplist.movies",
    domain: FavoriteDomain.Movie,
    titleId: "grow.games.toplist.movies.title",
    descriptionId: "grow.games.toplist.movies.description",
  }),
  toplistSeries: toplistGame({
    key: "toplist.series",
    domain: FavoriteDomain.Series,
    titleId: "grow.games.toplist.series.title",
    descriptionId: "grow.games.toplist.series.description",
  }),
  toplistCities: toplistGame({
    key: "toplist.cities",
    domain: FavoriteDomain.City,
    titleId: "grow.games.toplist.cities.title",
    descriptionId: "grow.games.toplist.cities.description",
  }),
  toplistDishes: toplistGame({
    key: "toplist.dishes",
    domain: FavoriteDomain.Dish,
    titleId: "grow.games.toplist.dishes.title",
    descriptionId: "grow.games.toplist.dishes.description",
  }),
  toplistHobbies: toplistGame({
    key: "toplist.hobbies",
    domain: FavoriteDomain.Hobby,
    titleId: "grow.games.toplist.hobbies.title",
    descriptionId: "grow.games.toplist.hobbies.description",
  }),
  toplistSports: toplistGame({
    key: "toplist.sports",
    domain: FavoriteDomain.Sport,
    titleId: "grow.games.toplist.sports.title",
    descriptionId: "grow.games.toplist.sports.description",
  }),
  toplistBooks: toplistGame({
    key: "toplist.books",
    domain: FavoriteDomain.Book,
    titleId: "grow.games.toplist.books.title",
    descriptionId: "grow.games.toplist.books.description",
  }),
  toplistMusic: toplistGame({
    key: "toplist.music",
    domain: FavoriteDomain.MusicArtist,
    titleId: "grow.games.toplist.music.title",
    descriptionId: "grow.games.toplist.music.description",
  }),
} as const satisfies Record<string, GameDefinition>;

export const GAME_LIST: readonly GameDefinition[] = Object.values(GAMES);

// Games that Grow · Play shows as one tile with a page of their own, the way a
// questionnaire group bundles its parts. Defined in code: unlike questionnaire
// groups, nothing about them is edited at runtime.
export const GAME_GROUPS = {
  top10: {
    key: "top10",
    titleId: "grow.games.groups.top10.title",
    descriptionId: "grow.games.groups.top10.description",
    imageSrc: "/games/top10-vinyl.jpg",
    games: [
      GAMES.toplistMovies,
      GAMES.toplistSeries,
      GAMES.toplistCities,
      GAMES.toplistDishes,
      GAMES.toplistHobbies,
      GAMES.toplistSports,
      GAMES.toplistBooks,
      GAMES.toplistMusic,
    ],
  },
} as const satisfies Record<string, GameGroupDefinition>;

export const GAME_GROUP_LIST: readonly GameGroupDefinition[] = Object.values(GAME_GROUPS);

export function gameByKey(key: string): GameDefinition | undefined {
  return GAME_LIST.find((game) => game.key === key);
}

export function gameGroupByKey(key: string): GameGroupDefinition | undefined {
  return GAME_GROUP_LIST.find((group) => group.key === key);
}

export function groupOfGame(gameKey: string): GameGroupDefinition | undefined {
  return GAME_GROUP_LIST.find((group) => group.games.some((game) => game.key === gameKey));
}

// The game that writes a favourites domain; the profile's "play again" leads there.
export function gameForDomain(domain: FavoriteDomain): GameDefinition | undefined {
  return GAME_LIST.find((game) => game.target.domain === domain);
}
